/**
 * Connect to Reverb after login + subscribe to user channel + public signals channel.
 *
 * Private channel (App.Models.User.{id}):
 *   trade.executed  — invalidates dashboard/positions/trades + optional toast
 *
 * Public channel (signals):
 *   signal.new      — FastAPI broadcasts each minute when a new ML prediction
 *                     lands. We invalidate the Signals and Charts caches so
 *                     the UI updates without waiting for the HTTP poll. The
 *                     poll remains as a backstop in case the WS misses a frame.
 */

import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { connectEcho, disconnectEcho, getEcho } from '../lib/echo';
import { api } from '../lib/api';
import { useUser } from './useAuth';
import type { TradeExecutedEvent } from '../types/api';

// Module-level "is the WS up?" — toggled by the connection handlers in
// useEchoSubscription. Consumed by useWsStatus() so any page can render
// a status pill without re-subscribing.
type WsState = 'connecting' | 'connected' | 'disconnected';
let _wsState: WsState = 'connecting';
const _wsListeners = new Set<(s: WsState) => void>();
function setWsState(s: WsState) {
  _wsState = s;
  _wsListeners.forEach((l) => l(s));
}

export function useWsStatus(): WsState {
  const [s, setS] = useState<WsState>(_wsState);
  useEffect(() => {
    _wsListeners.add(setS);
    return () => { _wsListeners.delete(setS); };
  }, []);
  return s;
}

interface SignalNewEvent {
  prediction_id: number;
  instrument_id: number;
  instrument_token: number;
  decision: 'buy' | 'sell' | 'hold' | 'no_trade';
  confidence: number;
  generated_at: string;
}

export interface TickEvent {
  instrument_token: number;
  last_price: number;
  volume: number;
  ts: string;  // ISO UTC
}

export interface TradingPausedEvent {
  reason: string;             // e.g. 'max_daily_loss'
  today_realized_inr: number;
  cap_inr: number;
  message: string;
}

// Reverb config — exposed via Vite env. Defaults are the dev values.
const REVERB_KEY    = import.meta.env.VITE_REVERB_APP_KEY    ?? 'ybnweauw13abahg2xw2s';
const REVERB_HOST   = import.meta.env.VITE_REVERB_HOST       ?? window.location.hostname;
const REVERB_PORT   = Number(import.meta.env.VITE_REVERB_PORT  ?? '443');
const REVERB_SCHEME = (import.meta.env.VITE_REVERB_SCHEME    ?? 'https') as 'http' | 'https';

export function useEchoSubscription(
  onTradeExecuted?: (e: TradeExecutedEvent) => void,
  onTradingPaused?: (e: TradingPausedEvent) => void,
) {
  const user = useUser();
  const qc = useQueryClient();
  const handlerRef = useRef(onTradeExecuted);
  handlerRef.current = onTradeExecuted;
  const pausedHandlerRef = useRef(onTradingPaused);
  pausedHandlerRef.current = onTradingPaused;

  useEffect(() => {
    if (!user) return;

    const echo = connectEcho({
      appKey: REVERB_KEY,
      host: REVERB_HOST,
      port: REVERB_PORT,
      scheme: REVERB_SCHEME === 'https' ? 'wss' : 'ws',
    });

    setWsState('connecting');
    const connector: any = (echo as any).connector;
    // Pusher.js (under Echo) exposes the raw socket via connector.pusher.connection
    const conn = connector?.pusher?.connection;
    // Track whether we've ever been connected. The first 'connected'
    // transition is just the initial handshake; subsequent ones are
    // RECONNECTS after a network drop, Reverb restart, browser tab
    // throttled in background, etc. On a reconnect we don't know which
    // events were missed during the gap, so we invalidate everything
    // the user is currently looking at. This is the self-healing
    // mechanism that lets us drop API polling entirely — a WS hiccup
    // self-corrects within ~1s of reconnect instead of leaving the UI
    // stuck on stale data forever.
    let everConnected = false;
    if (conn) {
      const onState = (states: { current: string; previous?: string }) => {
        if (states.current === 'connected') {
          setWsState('connected');
          if (everConnected) {
            // This is a reconnect. Catch up.
            qc.invalidateQueries({ queryKey: ['dashboard'] });
            qc.invalidateQueries({ queryKey: ['positions'] });
            qc.invalidateQueries({ queryKey: ['trades'] });
            qc.invalidateQueries({ queryKey: ['signals'] });
            qc.invalidateQueries({ queryKey: ['chart'] });
          }
          everConnected = true;
        }
        else if (states.current === 'unavailable')  setWsState('disconnected');
        else if (states.current === 'failed')       setWsState('disconnected');
        else if (states.current === 'disconnected') setWsState('disconnected');
        else                                         setWsState('connecting');
      };
      conn.bind('state_change', onState);
      // Seed from current state in case we missed the first transition
      if (conn.state === 'connected') {
        setWsState('connected');
        everConnected = true;
      }
    }

    // Browser-tab visibility: when the user comes back to the tab after
    // it was hidden long enough for Pusher's heartbeat to expire (~2 min),
    // we may already be in 'disconnected' state. Force a reconnect attempt
    // here so the user doesn't have to manually reload. If we're already
    // connected this is a no-op (the catchup runs on the state_change).
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && conn) {
        if (conn.state !== 'connected') {
          try { connector?.pusher?.connect?.(); } catch { /* best-effort */ }
        } else {
          // Already connected — but we may have been throttled. Catch up
          // anyway, cheaply, since stale-while-revalidate is the worst case.
          qc.invalidateQueries({ queryKey: ['positions'] });
          qc.invalidateQueries({ queryKey: ['dashboard'] });
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    const userChannel = echo.private(`App.Models.User.${user.id}`);
    userChannel.listen('.trade.executed', (e: TradeExecutedEvent) => {
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['positions'] });
      qc.invalidateQueries({ queryKey: ['trades'] });
      handlerRef.current?.(e);
    });
    // Auto-trading paused (e.g. daily-loss cap hit). Refresh the dashboard,
    // then surface it to the user via the page-supplied handler (which also
    // refreshes the auth user so the Settings auto-trade toggle flips to off).
    userChannel.listen('.trading.paused', (e: TradingPausedEvent) => {
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      pausedHandlerRef.current?.(e);
    });

    const signalsChannel = echo.channel('signals');
    signalsChannel.listen('.signal.new', (e: SignalNewEvent) => {
      // Refresh the Signals page list
      qc.invalidateQueries({ queryKey: ['signals'] });
      // Refresh the Charts query for the instrument this signal is about —
      // a new minute prediction means a new bar has closed.
      qc.invalidateQueries({ queryKey: ['chart', e.instrument_id] });
    });

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      echo.leave(`private-App.Models.User.${user.id}`);
      echo.leave('signals');
    };
  }, [user, qc]);
}

// ── Broadcast-interest keep-alive (2026-10-15) ─────────────────────────
// FastAPI only pushes a token's ticks to Reverb while some client has
// marked interest in it in the last 30 min, and that interest lives in
// FastAPI's memory, so every FastAPI restart empties it. Pages that marked
// once on load (Watchlists, Trade search) went silent after 30 min or after
// any restart; Signals, MiniChart and ManualTradeModal never marked at all
// and only streamed when some other page happened to. Every tick hook now
// retains its tokens here: new tokens are marked straight away (batched),
// and everything still on screen is re-marked every 3 minutes, so a page
// recovers within 3 min of a restart and never ages out while open.
const LIVE_KEEPALIVE_MS = 3 * 60_000;
const _liveRefs = new Map<number, number>();
let _livePending = new Set<number>();
let _liveFlush: ReturnType<typeof setTimeout> | null = null;
let _liveKeepAlive: ReturnType<typeof setInterval> | null = null;

async function markLive(tokens: number[]): Promise<void> {
  for (let i = 0; i < tokens.length; i += 200) {
    try {
      await api.post('/instruments/ensure-live', { instrument_tokens: tokens.slice(i, i + 200) });
    } catch {
      // Non-fatal: the next keep-alive retries.
    }
  }
}

function retainLive(tokens: number[]): void {
  tokens.forEach((t) => {
    const n = _liveRefs.get(t) ?? 0;
    _liveRefs.set(t, n + 1);
    if (n === 0) _livePending.add(t);
  });
  if (_livePending.size > 0 && !_liveFlush) {
    _liveFlush = setTimeout(() => {
      _liveFlush = null;
      const batch = [..._livePending];
      _livePending = new Set();
      void markLive(batch);
    }, 250);
  }
  if (!_liveKeepAlive && _liveRefs.size > 0) {
    _liveKeepAlive = setInterval(() => {
      if (_liveRefs.size > 0) void markLive([..._liveRefs.keys()]);
    }, LIVE_KEEPALIVE_MS);
  }
}

function releaseLive(tokens: number[]): void {
  tokens.forEach((t) => {
    const n = (_liveRefs.get(t) ?? 0) - 1;
    if (n > 0) _liveRefs.set(t, n);
    else { _liveRefs.delete(t); _livePending.delete(t); }
  });
  if (_liveRefs.size === 0 && _liveKeepAlive) {
    clearInterval(_liveKeepAlive);
    _liveKeepAlive = null;
  }
}

/**
 * Subscribe to live tick stream for ONE instrument. Returns the latest tick
 * (or null until the first one arrives). The Echo connection from
 * useEchoSubscription must be live — this hook just attaches a listener
 * to the existing connector.
 */
/**
 * Subscribe to MANY instruments at once and get a token -> tick map.
 *
 * Why this exists alongside the single-token hook: on a list page every row
 * used to subscribe independently, and Echo's `leave(channel)` tears a channel
 * down for EVERY listener. Two components on the same instrument meant the
 * first to unmount silenced the second. One subscription owned by the parent
 * removes that class of bug, and it lets the parent compute live TOTALS —
 * without it the header summed the API's stored unrealised P&L while the rows
 * showed live values, so the same quantity appeared twice with two answers
 * (operator screenshot, 2026-07-30).
 *
 * The token list is compared by value, so a re-render with the same
 * instruments does not churn subscriptions.
 */
export function useInstrumentTicks(tokens: Array<number | null | undefined>): Record<number, TickEvent> {
  const [ticks, setTicks] = useState<Record<number, TickEvent>>({});
  const key = tokens.filter((t): t is number => !!t).sort((a, b) => a - b).join(',');

  useEffect(() => {
    const list = key ? key.split(',').map(Number) : [];
    if (list.length === 0) {
      setTicks({});
      return;
    }
    let cancelled = false;
    const attached: string[] = [];
    let timer: ReturnType<typeof setTimeout> | null = null;
    retainLive(list);

    const attach = () => {
      if (cancelled) return;
      const echo = getEcho();
      if (!echo) {                 // Echo may still be handshaking — retry.
        timer = setTimeout(attach, 500);
        return;
      }
      list.forEach((token) => {
        const name = `instrument.${token}`;
        echo.channel(name).listen('.tick', (e: TickEvent) => {
          setTicks((prev) => ({ ...prev, [e.instrument_token]: e }));
        });
        attached.push(name);
      });
    };
    attach();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      const echo = getEcho();
      attached.forEach((name) => echo?.leave(name));
      releaseLive(list);
    };
  }, [key]);

  return ticks;
}

export function useInstrumentTickStream(instrumentToken: number | undefined): TickEvent | null {
  const [tick, setTick] = useState<TickEvent | null>(null);
  useEffect(() => {
    // Clear on EVERY token change, not just on undefined. Without this,
    // navigating spot-chart -> option-chart left the last SPOT tick in
    // state, and the chart rolled a live candle at the spot price onto an
    // option series (2026-08-10 tester: option at ~239 with a 24,608 bar —
    // Y-axis exploded, candles flattened invisible).
    setTick(null);
    if (!instrumentToken) {
      return;
    }
    // 2026-07-20 fix: the old version read getEcho() ONCE and bailed if
    // the singleton wasn't connected yet. Pages that mount before Echo
    // finishes its handshake (Positions right after login/refresh — the
    // common case) then NEVER attached the tick listener, so the row
    // silently degraded to 5s polling for the whole session — the
    // recurring "price not updating during the trade" report. Retry the
    // attach until the singleton exists; public channels need no auth.
    let cancelled = false;
    let attached: string | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    retainLive([instrumentToken]);
    const attach = () => {
      if (cancelled) return;
      const echo = getEcho();
      if (!echo) {
        timer = setTimeout(attach, 500);
        return;
      }
      const channelName = `instrument.${instrumentToken}`;
      echo.channel(channelName).listen('.tick', (e: TickEvent) => {
        // Belt: never accept a tick for a different instrument, whatever
        // the channel plumbing delivers.
        if (Number(e.instrument_token) === Number(instrumentToken)) setTick(e);
      });
      attached = channelName;
    };
    attach();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (attached) getEcho()?.leave(attached);
      releaseLive([instrumentToken]);
    };
  }, [instrumentToken]);
  return tick;
}

/**
 * Freshness of a tick stream: {stale, ageSec}. Same 90-second rule the
 * Positions page uses — no tick yet, or a tick older than 90s, counts as
 * stale. A price shown without this marker LOOKS live even when the feed
 * died (the 2026-07-30 09:15-09:44 blackout made that failure mode
 * visible), so every page that renders a live price should render this too.
 *
 * Re-evaluates on a 30s interval: a row whose feed dies stops re-rendering
 * on its own, so without the timer the marker could never appear.
 */
export function useTickStaleness(tick: TickEvent | null | undefined): { stale: boolean; ageSec: number | null } {
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);
  const ageSec = tick?.ts
    ? Math.max(0, Math.round((Date.now() - new Date(tick.ts).getTime()) / 1000))
    : null;
  const stale = tick?.last_price == null || (ageSec != null && ageSec > 90);
  return { stale, ageSec };
}

/** Disconnect Echo when the auth user goes away (logout, session expiry). */
export function useEchoLifecycle() {
  const user = useUser();
  useEffect(() => {
    return () => {
      if (!user) disconnectEcho();
    };
  }, [user]);
}
